import { useCallback, useEffect, useRef } from 'react';

import { useLocation } from 'react-router';

import { useAtomValue, useStore } from 'jotai';

import { useLocationChange } from '@/hooks/useLocationChange';

import { debugSearchSessionAtom } from '@/components/developer-menu/state';

import Constants from '@/constants';

import {
  resetSearchSessionAtom,
  searchSessionAtom,
  type SearchSessionState,
} from './searchSession.state';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { userInAISearchPOCAtom } from '@/atoms/ai-search-poc';

const SEARCH_PATH = `/${Constants.ROUTE_PATH_NAMES.SEARCH_PATH_NAME}`;
const DOC_PATH = `/${Constants.ROUTE_PATH_NAMES.DOCUMENT_PATH_NAME}`;
const DOC_INACTIVITY_MS = 60_000;

const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
] as const;

const isSearchRoute = (pathname: string) => pathname.startsWith(SEARCH_PATH);
const isDocRoute = (pathname: string) => pathname.startsWith(DOC_PATH);

export const useSearchSessionTracker = () => {
  const store = useStore();
  const track = useAmplitudeTrack();
  const location = useLocation();
  useAtomValue(searchSessionAtom);

  const trackRef = useRef(track);

  useEffect(() => {
    trackRef.current = track;
  }, [track]);

  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const journalRef = useRef<
    {
      time: number;
      event: string;
      accumulatedActiveMs: number;
      timerStartedAt: number | null;
    }[]
  >([]);

  const logJournal = useCallback(
    (event: string, s: SearchSessionState) => {
      if (!store.get(debugSearchSessionAtom)) return;
      journalRef.current.push({
        time: Date.now(),
        event,
        accumulatedActiveMs: s.accumulatedActiveMs,
        timerStartedAt: s.timerStartedAt,
      });
    },
    [store],
  );

  const setSession = (
    updater: (prev: SearchSessionState) => SearchSessionState,
  ) => {
    store.set(searchSessionAtom, updater(store.get(searchSessionAtom)));
  };

  const fireEvent = () => {
    const trackFn = trackRef.current;
    const snapshot = store.get(searchSessionAtom);
    if (!trackFn || !snapshot.active) return;

    const elapsed =
      snapshot.timerStartedAt !== null
        ? Date.now() - snapshot.timerStartedAt
        : 0;
    const duration_seconds = Math.round(
      (snapshot.accumulatedActiveMs + elapsed) / 1000,
    );

    const payload = {
      duration_seconds,
      search_count: snapshot.searchCount,
      resources_clicked: snapshot.resourceClicks,
      smart_search_enabled: store.get(userInAISearchPOCAtom),
    };

    if (store.get(debugSearchSessionAtom)) {
      logJournal('fire', snapshot);
      const origin = journalRef.current[0]?.time ?? Date.now();
      console.debug('[search_time_spent]', payload);
      console.table(
        journalRef.current.map(entry => {
          const liveSegment =
            entry.timerStartedAt !== null
              ? entry.time - entry.timerStartedAt
              : 0;
          return {
            wallClock: `+${((entry.time - origin) / 1000).toFixed(1)}s`,
            event: entry.event,
            totalActiveMs: entry.accumulatedActiveMs + liveSegment,
            timerRunning: entry.timerStartedAt !== null,
          };
        }),
      );
      journalRef.current = [];
    }

    trackFn('search_time_spent', payload);
  };

  useLocationChange((curr, prev) => {
    if (!curr?.pathname) return;

    const currIsSearch = isSearchRoute(curr.pathname);
    const currIsDoc = isDocRoute(curr.pathname);
    const prevIsSearch = prev?.pathname ? isSearchRoute(prev.pathname) : false;
    const prevIsDoc = prev?.pathname ? isDocRoute(prev.pathname) : false;

    if (currIsSearch && prevIsDoc) {
      setSession(s => {
        if (s.timerStartedAt !== null || !s.active) return s;
        const next = { ...s, timerStartedAt: Date.now() };
        logJournal('doc→search resume', next);
        return next;
      });
      return;
    }

    if (currIsSearch && !prevIsSearch) {
      setSession(s => {
        if (s.active) {
          if (s.timerStartedAt !== null) return s;
          const next = { ...s, timerStartedAt: Date.now() };
          logJournal('re-enter search resume', next);
          return next;
        }
        const next: SearchSessionState = {
          active: true,
          timerStartedAt: Date.now(),
          accumulatedActiveMs: 0,
          searchCount: s.searchCount,
          resourceClicks: 0,
        };
        journalRef.current = [];
        logJournal('enter search', next);
        return next;
      });
      return;
    }

    if (currIsDoc && !prevIsDoc) {
      logJournal('navigate to doc', store.get(searchSessionAtom));
    }

    if (!currIsSearch && !currIsDoc && (prevIsSearch || prevIsDoc)) {
      fireEvent();
      store.set(resetSearchSessionAtom);
      return;
    }
  });

  // Pause the session timer after 1 minute of inactivity while on a document route.
  // Any user interaction resets the countdown.
  useEffect(() => {
    if (!isDocRoute(location.pathname)) return;

    const pauseTimer = () => {
      store.set(searchSessionAtom, s => {
        if (s.timerStartedAt === null) return s;
        const elapsed = Date.now() - s.timerStartedAt;
        const next = {
          ...s,
          accumulatedActiveMs: s.accumulatedActiveMs + elapsed,
          timerStartedAt: null,
        };
        logJournal('idle 60s → pause', next);
        return next;
      });
    };

    const startInactivityTimer = () => {
      store.set(searchSessionAtom, s => {
        if (s.timerStartedAt !== null || !s.active) return s;
        const next = { ...s, timerStartedAt: Date.now() };
        logJournal('activity → resume', next);
        return next;
      });
      if (inactivityTimerRef.current !== null) {
        clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = setTimeout(pauseTimer, DOC_INACTIVITY_MS);
    };

    startInactivityTimer();
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, startInactivityTimer, { passive: true });
    });

    return () => {
      if (inactivityTimerRef.current !== null) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, startInactivityTimer);
      });
    };
  }, [location.pathname, store, logJournal]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      fireEvent();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  });
};
